// src/lib/rlu_diff.ts — DIFERENȚA dintre două versiuni ale regulamentului (PUR, partajat
// server↔client, zero dependințe). Sursa istoricului din #/admin → Regim UTR (REQ-URB-028/056:
// „editare … cu arhivarea și păstrarea istoricului").
//
// Se compară DOAR subarborii regulamentului — `rlu.utr_indicatori` (pe codul `utr`), `rlu.avize`
// (pe codul zonei) și `rlu_fise` (cod → rubrici) — nu tot `metadata`: îmbinarea metadatelor e superficială,
// iar `metadata` mai ține bilanțuri, obiective, sursa fișierului; fără această restrângere fiecare
// rând al istoricului ar fi părut „s-a schimbat tot". Restul cheilor sunt doar SEMNALATE
// (`other_rlu` / `other_meta`), ca să se vadă că versiunea există și de ce.
//
// ⚠️ Oricare parte poate LIPSI (versiunea `create` n-are regulament; o legislație retrasă e un rând
// EAV cu valoare `null`): fiecare funcție primește `unknown` și tratează absența ca „gol".
//
// ⚠️ Valorile se NORMALIZEAZĂ înainte de comparare: importul 904 scrie doar rubricile pe care le are
// (`{utr, denumire, pot_max}`), editorul din admin le scrie pe toate, cu șir gol, iar xlsx-ul poate
// da `null` sau „35" ca text. Fără normalizare, prima salvare din admin după un import ar fi raportat
// schimbări-fantomă („CUT max: — → —") pe fiecare UTR. Lipsă ≡ null ≡ "" ≡ „  "; „35" ≡ 35; „0,45" ≡ 0.45.

import { parse_avize, parse_legislatie } from "./rlu"

export interface KeyedDiff {
	added: string[]
	removed: string[]
	changed: string[]
}
export interface FieldChange {
	key: string
	before: unknown // undefined = rubrica nu exista
	after: unknown // undefined = rubrica a dispărut
}
export interface CodeChange {
	cod: string
	kind: "added" | "removed" | "changed"
	fields: FieldChange[] // la added/removed: toate rubricile (before/after pe o singură parte)
}
export interface RluSummary {
	utr: KeyedDiff
	fise: KeyedDiff
	avize: KeyedDiff
	other_rlu: string[] // chei din `rlu` (fără utr_indicatori/avize) cu conținut diferit
	other_meta: string[] // chei din `metadata` (fără rlu/rlu_fise) cu conținut diferit
}
export interface RluDiff {
	summary: RluSummary
	touches_rlu: boolean // atinge regulamentul (indicatori, fișe, avize sau altă cheie din rlu)
	utr: CodeChange[]
	fise: CodeChange[]
	avize: CodeChange[]
}

/** Etichetele rubricilor de indicatori, pentru afișare (cheile sunt cele din `utr_indicatori`). */
export const INDICATOR_LABELS: Record<string, string> = {
	utr: "Cod UTR",
	denumire: "Denumire",
	pot_max: "POT max",
	cut_max: "CUT max",
	rh_max: "Rh max",
	cod_szf: "Cod SZF",
	denumire_szf: "Denumire SZF",
}

const empty_keyed = (): KeyedDiff => ({ added: [], removed: [], changed: [] })
export const EMPTY_KEYED_DIFF: KeyedDiff = Object.freeze({
	added: Object.freeze([]) as unknown as string[],
	removed: Object.freeze([]) as unknown as string[],
	changed: Object.freeze([]) as unknown as string[],
})
export const EMPTY_RLU_SUMMARY: RluSummary = Object.freeze({
	utr: EMPTY_KEYED_DIFF,
	fise: EMPTY_KEYED_DIFF,
	avize: EMPTY_KEYED_DIFF,
	other_rlu: Object.freeze([]) as unknown as string[],
	other_meta: Object.freeze([]) as unknown as string[],
})
export const EMPTY_RLU_DIFF: RluDiff = Object.freeze({
	summary: EMPTY_RLU_SUMMARY,
	touches_rlu: false,
	utr: Object.freeze([]) as unknown as CodeChange[],
	fise: Object.freeze([]) as unknown as CodeChange[],
	avize: Object.freeze([]) as unknown as CodeChange[],
})

function is_obj(x: unknown): x is Record<string, unknown> {
	return typeof x === "object" && x !== null && !Array.isArray(x)
}
const obj = (x: unknown): Record<string, unknown> => (is_obj(x) ? x : {})

/** Serializare STABILĂ (chei sortate) — egalitatea nu depinde de ordinea cheilor din JSON. */
function stable(v: unknown): string {
	if (Array.isArray(v)) {
		return "[" + v.map(stable).join(",") + "]"
	}
	if (is_obj(v)) {
		return (
			"{" +
			Object.keys(v)
				.toSorted()
				.map((k) => JSON.stringify(k) + ":" + stable(v[k]))
				.join(",") +
			"}"
		)
	}
	return v === undefined ? "undefined" : JSON.stringify(v)
}

/** Forma canonică a unei valori de rubrică (vezi antetul): gol → undefined, numeric ca text → număr. */
const NUMERIC_TEXT = /^-?\d+(?:[.,]\d+)?$/
function norm(v: unknown): unknown {
	if (v === null || v === undefined) {
		return undefined
	}
	if (typeof v === "string") {
		const t = v.trim()
		if (t === "") {
			return undefined
		}
		return NUMERIC_TEXT.test(t) ? Number(t.replace(",", ".")) : t
	}
	return v
}

function field_changes(a: Record<string, unknown>, b: Record<string, unknown>): FieldChange[] {
	const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].toSorted()
	const out: FieldChange[] = []
	for (const key of keys) {
		const na = norm(a[key])
		const nb = norm(b[key])
		if (na === undefined && nb === undefined) {
			continue // rubrică goală pe ambele părți, indiferent de formă
		}
		if (stable(na) !== stable(nb)) {
			out.push({ key, before: a[key], after: b[key] })
		}
	}
	return out
}

/**
 * Cheia unui cod, cu dublurile păstrate VIZIBILE („Lc", „Lc#2", „Lc#3"): editorul lasă același cod pe
 * două rânduri, iar „primul câștigă" ar fi ascuns orice editare a celui de-al doilea.
 */
function unique_key(seen: Map<string, number>, cod: string): string {
	const n = seen.get(cod) ?? 0
	seen.set(cod, n + 1)
	return n === 0 ? cod : `${cod}#${n + 1}`
}

function keyed_diff(
	prev: Map<string, Record<string, unknown>>,
	next: Map<string, Record<string, unknown>>,
): { keyed: KeyedDiff; changes: CodeChange[] } {
	const keyed = empty_keyed()
	const changes: CodeChange[] = []
	const codes = [...new Set([...prev.keys(), ...next.keys()])].toSorted()
	for (const cod of codes) {
		const a = prev.get(cod)
		const b = next.get(cod)
		if (a && !b) {
			keyed.removed.push(cod)
			changes.push({ cod, kind: "removed", fields: field_changes(a, {}) })
		} else if (!a && b) {
			keyed.added.push(cod)
			changes.push({ cod, kind: "added", fields: field_changes({}, b) })
		} else if (a && b) {
			const fields = field_changes(a, b)
			if (fields.length > 0) {
				keyed.changed.push(cod)
				changes.push({ cod, kind: "changed", fields })
			}
		}
	}
	return { keyed, changes }
}

/** Indicatorii pe codul `utr` (trim; dublurile ca „cod#2"; fără cod = ignorat). */
function index_indicatori(rlu: Record<string, unknown>): Map<string, Record<string, unknown>> {
	const out = new Map<string, Record<string, unknown>>()
	const seen = new Map<string, number>()
	const rows = Array.isArray(rlu.utr_indicatori) ? rlu.utr_indicatori : []
	for (const raw of rows) {
		const row = obj(raw)
		const cod = String(row.utr ?? "").trim()
		if (cod) {
			out.set(unique_key(seen, cod), row)
		}
	}
	return out
}

/**
 * Avizele pe codul zonei (inclusiv „*”), normalizate la `{avize: string[]}` ca să nu conteze ambalajul.
 * Prin ACELAȘI parser ca panoul de regim și actele (`parse_avize`, lib/rlu): codul zonei poate sta sub
 * `utr` / `cod_zf` / `cod_szf` / `cod`, iar lista poate fi și text separat prin `;`. Până la 01.10 se
 * citea DOAR cheia `cod`: avizele scrise ca `{utr: "C", avize: […]}` (forma din tabelele 904 și din
 * datele puse prin API) erau invizibile — nici istoricul nu le arăta schimbările, nici garda de pierderi
 * (`regulament_losses`) nu le-ar fi apărat. O intrare fără niciun aviz nu există (ca la regim).
 */
function index_avize(rlu: Record<string, unknown>): Map<string, Record<string, unknown>> {
	const out = new Map<string, Record<string, unknown>>()
	const seen = new Map<string, number>()
	for (const e of parse_avize(rlu.avize)) {
		out.set(unique_key(seen, e.cod), { avize: e.avize })
	}
	return out
}

function index_fise(meta: Record<string, unknown>): Map<string, Record<string, unknown>> {
	const out = new Map<string, Record<string, unknown>>()
	for (const [cod, fisa] of Object.entries(obj(meta.rlu_fise))) {
		const key = cod.trim()
		if (key && !out.has(key)) {
			out.set(key, obj(fisa))
		}
	}
	return out
}

function differing_keys(
	a: Record<string, unknown>,
	b: Record<string, unknown>,
	skip: ReadonlySet<string>,
): string[] {
	return [...new Set([...Object.keys(a), ...Object.keys(b)])]
		.filter((k) => !skip.has(k) && stable(a[k]) !== stable(b[k]))
		.toSorted()
}

const RLU_COMPARED = new Set(["utr_indicatori", "avize"])
const META_COMPARED = new Set(["rlu", "rlu_fise"])

/** Diferența dintre două `metadata` de hartă, restrânsă la regulament. */
export function diff_regulament(prev_meta: unknown, next_meta: unknown): RluDiff {
	const pm = obj(prev_meta)
	const nm = obj(next_meta)
	const prlu = obj(pm.rlu)
	const nrlu = obj(nm.rlu)
	const utr = keyed_diff(index_indicatori(prlu), index_indicatori(nrlu))
	const fise = keyed_diff(index_fise(pm), index_fise(nm))
	const avize = keyed_diff(index_avize(prlu), index_avize(nrlu))
	const other_rlu = differing_keys(prlu, nrlu, RLU_COMPARED)
	const other_meta = differing_keys(pm, nm, META_COMPARED)
	const summary: RluSummary = {
		utr: utr.keyed,
		fise: fise.keyed,
		avize: avize.keyed,
		other_rlu,
		other_meta,
	}
	return {
		summary,
		touches_rlu:
			utr.changes.length > 0 ||
			fise.changes.length > 0 ||
			avize.changes.length > 0 ||
			other_rlu.length > 0,
		utr: utr.changes,
		fise: fise.changes,
		avize: avize.changes,
	}
}

/**
 * Ce ar PIERDE regulamentul dacă `next_meta` ar înlocui `prev_meta` — garda scrierilor făcute de un
 * agent (MCP `update_map`). Îmbinarea metadatelor e pe primul nivel, deci un `rlu` trimis doar cu
 * rândul modificat ar șterge tăcut celelalte UTR-uri, avizele și restul cheilor; un `rlu_fise` cu o
 * singură fișă le-ar șterge pe celelalte.
 */
export interface RluLosses {
	utr: string[] // coduri de UTR (rânduri de indicatori) care ar dispărea
	fise: string[] // fișe RLU care ar dispărea
	avize: string[] // coduri de zonă ale căror avize ar dispărea
	rlu_keys: string[] // alte chei ÎNTREGI din `rlu` care ar dispărea (zone_protectie, zone_functionale…)
}
export const NO_RLU_LOSSES: RluLosses = Object.freeze({
	utr: Object.freeze([]) as unknown as string[],
	fise: Object.freeze([]) as unknown as string[],
	avize: Object.freeze([]) as unknown as string[],
	rlu_keys: Object.freeze([]) as unknown as string[],
})

/** Valoarea poartă ceva? (lipsă / null / șir gol / listă goală / obiect gol = nimic de pierdut) */
function has_content(v: unknown): boolean {
	if (v === null || v === undefined) {
		return false
	}
	if (typeof v === "string") {
		return v.trim() !== ""
	}
	if (Array.isArray(v)) {
		return v.length > 0
	}
	return is_obj(v) ? Object.keys(v).length > 0 : true
}

/**
 * Pierderile unei scrieri, mai puțin cele declarate în `allowed` (parametrul `remove_codes` al
 * apelului). Aceeași indexare ca diferența din istoric (`diff_regulament`), deci „ce ar dispărea” aici
 * e exact ce ar apărea acolo ca „șters”. Un cod din `allowed` acoperă și dublurile lui („Lc” → „Lc#2”);
 * o cheie din `rlu` se declară cu numele ei („zone_protectie” sau „rlu.zone_protectie”).
 */
export function regulament_losses(
	prev_meta: unknown,
	next_meta: unknown,
	allowed: readonly string[] = [],
): RluLosses {
	const d = diff_regulament(prev_meta, next_meta)
	const allow = new Set(allowed.map((c) => String(c ?? "").trim()).filter((c) => c !== ""))
	const kept = (codes: string[]): string[] =>
		codes.filter((c) => !allow.has(c) && !allow.has(c.replace(/#\d+$/, "")))
	const prlu = obj(obj(prev_meta).rlu)
	const nrlu = obj(obj(next_meta).rlu)
	const rlu_keys = Object.keys(prlu)
		.filter((k) => !RLU_COMPARED.has(k) && has_content(prlu[k]) && !has_content(nrlu[k]))
		.filter((k) => !allow.has(k) && !allow.has(`rlu.${k}`))
		.toSorted()
	return {
		utr: kept(d.summary.utr.removed),
		fise: kept(d.summary.fise.removed),
		avize: kept(d.summary.avize.removed),
		rlu_keys,
	}
}

/** Câte pierderi (0 = scrierea nu elimină nimic nedeclarat). */
export function losses_count(l: RluLosses): number {
	return l.utr.length + l.fise.length + l.avize.length + l.rlu_keys.length
}

/** Pierderile, numite — textul refuzului („UTR: L1, L2; fișe: C; avize: *”). */
export function losses_text(l: RluLosses): string {
	const parts: string[] = []
	if (l.utr.length > 0) {
		parts.push("UTR: " + l.utr.join(", "))
	}
	if (l.fise.length > 0) {
		parts.push("fișe: " + l.fise.join(", "))
	}
	if (l.avize.length > 0) {
		parts.push("avize (coduri de zonă): " + l.avize.join(", "))
	}
	if (l.rlu_keys.length > 0) {
		parts.push("chei din rlu: " + l.rlu_keys.join(", "))
	}
	return parts.join("; ")
}

/**
 * Nomenclatorul legislativ, prin ACELAȘI parser ca panoul de regim și actele (lib/rlu) → intrări pe
 * `act`; o intrare fără act (păstrată de parser, cu descriere) se cheie pe descriere.
 */
function index_legislatie(v: unknown): Map<string, Record<string, unknown>> {
	const out = new Map<string, Record<string, unknown>>()
	const seen = new Map<string, number>()
	for (const e of parse_legislatie(v)) {
		const key = e.act || `(fără act) ${e.descriere}`
		out.set(unique_key(seen, key), { descriere: e.descriere, url: e.url })
	}
	return out
}

/** Diferența dintre două valori ale `attributes.legislatie` (acte adăugate / retrase / modificate). */
export function diff_legislatie(prev: unknown, next: unknown): KeyedDiff {
	return keyed_diff(index_legislatie(prev), index_legislatie(next)).keyed
}

const keyed_count = (d: KeyedDiff): number => d.added.length + d.removed.length + d.changed.length

/** Numărul total de schimbări dintr-un rezumat (0 = versiunea nu atinge regulamentul). */
export function summary_count(s: RluSummary): number {
	return keyed_count(s.utr) + keyed_count(s.fise) + keyed_count(s.avize) + s.other_rlu.length
}
